
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { BaseStation, NetworkUptimeReport, FullAlert, Enterprise, EnrolProfile, EnrolProfileActility } from '@/types';

export class ApiClient {

  private baseConfig: AxiosRequestConfig = {
    timeout: 30000,
    validateStatus: () => true,
    headers: {}
  };

  public async getBaseStations(): Promise<GetBaseStationsReply & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `/v2/api/base-stations`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    let baseStationList = resp.data as BaseStation[];

    // we make sure the LRR ID is a string
    baseStationList = baseStationList.map(bss => { bss.lrrId = `` + bss.lrrId; return bss});

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, baseStationList: baseStationList};
  }

  public async getUptimeReport(req:GetUptimeReportReq): Promise<GetUptimeReportReply & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `/v2/api/base-stations/uptime-perc`,
      params: {
        _ts: new Date().getTime(),
        startTs: req.startTs,
        endTs: req.endTs,
        searchList: req.searchList && req.searchList !== '' ? req.searchList : undefined,
        excludeList: req.excludeList && req.excludeList !== '' ? req.excludeList : undefined,
        minOutageSec: req.minOutageSec ? req.minOutageSec : undefined,
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, report: resp.data as NetworkUptimeReport};
  }

  public async getAlerts(req:GetAlertsReq): Promise<GetAlertsReply & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `/v2/api/alerts`,
      params: {
        _ts: new Date().getTime(),
        skip: req.skip,
        limit: req.limit,
        activeOnly: req.activeOnly
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, alertList: resp.data as FullAlert[]};
  }

  public async clearAlert(req:ClearAlertReq): Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `/v2/api/alerts/${req.alertId}/clear`
    });

    const resp = await this.doRequest(cfg, 204);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  // ADMIN section

  public async getEnterprises(req:GetEntListReq): Promise<GetEntListReply & HttpResponse> {
    
    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `/v2/api/admin/enterprises`,
      params: {
        _ts: new Date().getTime(),
        skip: req.skip,
        limit: req.limit
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, entList: resp.data as Enterprise[]};
  }

  
  public async createEnterprise(req:NewEntReq):Promise<HttpResponse & {newEnterprise?:Enterprise}> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `/v2/api/admin/enterprises`,
      data: req,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, newEnterprise: resp.data as Enterprise};
  }

  public async updateEnterpriseDataStorage(req:{enterpriseId:string, dataStorage:boolean, dataDecoding:boolean}):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `/v2/api/admin/enterprises/${req.enterpriseId}/data-management`,
      data: {
        dataStorage: req.dataStorage,
        dataDecoding: req.dataDecoding,
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async updateEnterpriseNwServer(req:{enterpriseId:string, subscriberId: number, enrolId?: string, supplierId?:string}):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `/v2/api/admin/enterprises/${req.enterpriseId}/nwserver-profile`,
      data: {
        actility:{
          subscriberId: req.subscriberId,
          supplierId: req.supplierId,
          enrolId: req.enrolId
        }
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async deleteEnterprise(enterpriseId:string):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `/v2/api/admin/enterprises/${enterpriseId}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async getEnrolProfiles(req:{skip:number, limit:number}): Promise<{enrolProfileList?:EnrolProfile[]} & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `/v2/api/admin/enrol-profiles`,
      params: {
        _ts: new Date().getTime(),
        skip: req.skip,
        limit: req.limit
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, enrolProfileList: resp.data as EnrolProfile[]};
  }

  public async createEnrolProfile(req:{enrolId:string, description:string, type:'actility', actility:EnrolProfileActility}):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `/v2/api/admin/enrol-profiles`,
      data: req,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async updateEnrolProfile(req:{enrolId:string, description:string, actility:EnrolProfileActility}):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `/v2/api/admin/enrol-profiles/${req.enrolId}`,
      data: {
        description: req.description,
        actility: req.actility,
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async deleteEnrolProfile(enrolId:string):Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `/v2/api/admin/enrol-profiles/${enrolId}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }
  
  private async doRequest(req: AxiosRequestConfig, expectedHttpCode?: number): Promise<HttpReply> {

    let resp: AxiosResponse;

    try {
      resp = await axios.request(req);
    } catch (err) {
      return {errorMsg: `${err}`};
    }

    // we expect a code 200 by default
    expectedHttpCode = expectedHttpCode !== undefined ? expectedHttpCode : 200;

    if (resp.status !== expectedHttpCode) {
      if (resp.data && resp.data.message) {
        return {httpCode: resp.status, errorMsg: `${resp.data.message} (HTTP ${resp.status})`};
      } else {
        return {httpCode: resp.status, errorMsg: `unexpected HTTP error ${resp.status}`};
      }
    }
    return Promise.resolve({data: resp.data});
  }
}

interface HttpReply {
  httpCode?: number;
  errorMsg?: string;
  data?: object;
}
export interface HttpResponse {
  httpCode?: number;
  errorMsg?: string;
}

export interface GetBaseStationsReply {
  baseStationList?: BaseStation[];
}

export interface GetUptimeReportReq {
  startTs: number;
  endTs: number;
  searchList?: string;
  excludeList?: string;
  minOutageSec?: number;
}

export interface GetUptimeReportReply {
  report?:NetworkUptimeReport;
}

export interface GetAlertsReq {
  skip: number;
  limit: number;
  activeOnly?: boolean;
}

export interface GetAlertsReply {
  alertList?:FullAlert[]
}

export interface ClearAlertReq {
  alertId:string;
}

export interface GetEntListReq {
  limit: number;
  skip: number;
}

export interface GetEntListReply {
  entList?:Enterprise[];
}

export interface NewEntReq {
  enterpriseId: string;
  dataStorage: boolean;
  dataDecoding: boolean;
  groupList?: string[];
  nwServer?: {
    actility?: {
      subscriberId: number;
      supplierId?: string;
      enrolId?: string;
    }
  }
}

export const curApiClient = new ApiClient();
